import React, { useState, useEffect, useRef } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Snackbar, Tooltip } from "@mui/material";
import {
  fetchJobById,
  getLanguages,
  generateDynamicVoicePrompt,
  updateJobHiringStageSettings,
} from "../../Redux/ActionCreators/jobsAction";
import { fetchVoiceAssistants } from "../../Redux/ActionCreators/candidateAction";
import { updateHiringAssistaniData } from "../../Redux/ActionCreators/qualifyQuestionsAction";

const TeleScreening = ({ setType, jobId: propJobId }) => {
  const { jobId: paramsJobId } = useParams();
  const jobId = propJobId || paramsJobId;
  const dispatch = useDispatch();

  const job = useSelector((state) => state.jobs.latestJob);

  const [newQuestion, setNewQuestion] = useState({
    voice_call_id: "",
    voice_call_name: "",
    additional_questions: "",
    custom_voice_call_qs_generation: "",
    custom_voice_call_evaluation_weights: {
      communication: 25,
      technical: 60,
      cultural_fit: 15,
    },
  });

  const [saveLoading, setSaveLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voiceAssistants, setVoiceAssistants] = useState([]);
  const [customLogisticsEnabled, setCustomLogisticsEnabled] = useState(false);
  const [enableInstructions, setEnableInstructions] = useState(false);
  const [customLogistics, setCustomLogistics] = useState([""]);
  const [customInstructions, setCustomInstructions] = useState("");
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [assistantsLoading, setAssistantsLoading] = useState(false);

  // Dynamic voice assistant (AI-generated screening call)
  const [dynamicVoiceEnabled, setDynamicVoiceEnabled] = useState(false);
  const [dynamicVoiceInputText, setDynamicVoiceInputText] = useState("");
  const [dynamicVoicePrompt, setDynamicVoicePrompt] = useState({
    opening_greeting: "",
    screening_script: "",
    end_call_rules: "",
    compiled_prompt: "",
  });
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [savingDynamicPrompt, setSavingDynamicPrompt] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [promptModal, setPromptModal] = useState({ open: false, title: "", field: null });
  const [evaluationFitStrategy, setEvaluationFitStrategy] = useState("");
  const [evaluationCriteriaEnabled, setEvaluationCriteriaEnabled] = useState(false);
  const evaluationCriteriaRef = useRef(null);

  const EVALUATION_LABELS = ["EXCELLENT_FIT", "GOOD_FIT", "NOT_FIT"];

  const audioRef = useRef(null);
  const [playingAudio, setPlayingAudio] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState("");
  const [audioObj, setAudioObj] = useState(null);
  const [playingAudioUrl, setPlayingAudioUrl] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });


  const resetForm = () => {
    setNewQuestion({
      voice_call_id: "",
      voice_call_name: "",
      additional_questions: "",
    });
    setSelectedLanguage("");
    setVoiceAssistants([]);
    setDynamicVoiceEnabled(false);
    setDynamicVoiceInputText("");
    setDynamicVoicePrompt({
      opening_greeting: "",
      screening_script: "",
      end_call_rules: "",
      compiled_prompt: "",
    });
    setShowPromptEditor(false);
    setEvaluationFitStrategy("");
    setEvaluationCriteriaEnabled(false);
    stopAudio();
  };

  const stopAudio = () => {
    if (audioObj) {
      audioObj.pause();
      audioObj.currentTime = 0;
    }
    setPlayingAudioUrl("");
  };

  const handleAudio = (url) => {
    if (!url) return;

    if (audioObj && playingAudioUrl === url) {
      if (audioObj.paused) {
        audioObj.play().catch((e) => {
          console.error("Error resuming audio:", e);
          showSnackbar("Failed to resume audio: " + e.message);
        });
      } else {
        audioObj.pause();
      }
      return;
    }

    if (audioObj) {
      audioObj.pause();
      audioObj.currentTime = 0;
    }

    console.log("Attempting to play audio from URL:", url);
    const newAudio = new Audio(url);

    newAudio.onplay = () => {
      setPlayingAudioUrl(url);
    };

    newAudio.onpause = () => {
      setPlayingAudioUrl("");
    };

    newAudio.onended = () => {
      setPlayingAudioUrl("");
    };

    newAudio.onerror = (e) => {
      console.error("Audio loading error:", e, newAudio.error);
      let errorMsg = "Failed to load audio source.";
      if (newAudio.error) {
        switch (newAudio.error.code) {
          case 1:
            errorMsg += " (Aborted)";
            break;
          case 2:
            errorMsg += " (Network Error)";
            break;
          case 3:
            errorMsg += " (Decoding Error)";
            break;
          case 4:
            errorMsg += " (Source Not Supported)";
            break;
          default:
            errorMsg += " (Unknown Error)";
        }
      }
      showSnackbar(errorMsg);
      setPlayingAudioUrl("");
    };

    newAudio
      .play()
      .then(() => {
        // Playback started
      })
      .catch((error) => {
        console.error("Play execution error:", error);
        showSnackbar("Playback failed: " + error.message);
        setPlayingAudioUrl("");
      });

    setAudioObj(newAudio);
  };

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioObj) audioObj.pause();
    };
  }, [audioObj]);

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const initialfetch = async () => {
    if (jobId) {
      setLoading(true);
      const DEFAULT_LANG_ID = "460182e9-b4f2-4fc3-8f4a-c8f1c4a4614e";

      try {
        const [jobRes, langRes, assistantsRes] = await Promise.all([
          dispatch(fetchJobById(jobId)),
          dispatch(getLanguages()),
          dispatch(fetchVoiceAssistants()),
        ]);

        let allAssistants = [];
        let languageSet = false;

        if (langRes?.success) {
          setLanguages(langRes.data);
        }

        if (assistantsRes?.success) {
          allAssistants = assistantsRes.data;
          // Initially set to all, but will be overwritten if hydration finds a specific language
          setVoiceAssistants(allAssistants);
        }

        // Hydration Logic from Job Data
        if (jobRes) {
          const voiceStage = jobRes?.hiring_stages?.find(
            (stage) =>
              stage?.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
          );

          if (voiceStage) {
            const assistantId = voiceStage?.data?.assistant_id;
            const additionalQuestions = voiceStage?.data?.additional_questions || "";
            const customPrompt =
              voiceStage?.data?.custom_voice_call_qs_generation || "";

            const weights =
              voiceStage?.data?.custom_voice_call_evaluation_weights || {
                communication: 25,
                technical: 60,
                cultural_fit: 15,
              };

            const logisticEnabled =
              voiceStage?.data?.custom_logistic_questions_enabled || false;
            const logisticQs =
              voiceStage?.data?.custom_logistic_questions?.map((q) => q.question) ||
              [];

            const instructionsEnabled =
              voiceStage?.data?.enable_instructions || false;
            const instructionsText =
              voiceStage?.data?.custom_instructions || "";

            setCustomLogisticsEnabled(logisticEnabled);
            setCustomLogistics(logisticEnabled ? logisticQs : []);
            setEnableInstructions(instructionsEnabled);
            setCustomInstructions(instructionsEnabled ? instructionsText : "");

            const dynamicVoiceAssistant = voiceStage?.data?.dynamic_voice_assistant;
            const isDynamicMode =
              voiceStage?.data?.telephonic_call_mode === "DYNAMIC_VOICE_CALL";
            setDynamicVoiceEnabled(isDynamicMode);
            setDynamicVoicePrompt({
              opening_greeting: dynamicVoiceAssistant?.opening_greeting || "",
              screening_script: dynamicVoiceAssistant?.screening_script || "",
              end_call_rules: dynamicVoiceAssistant?.end_call_rules || "",
              compiled_prompt: dynamicVoiceAssistant?.compiled_prompt || "",
            });
            setEvaluationFitStrategy(
              dynamicVoiceAssistant?.evaluation?.evaluation_fit_strategy || ""
            );
            setEvaluationCriteriaEnabled(
              dynamicVoiceAssistant?.evaluation?.enabled || false
            );
            setShowPromptEditor(
              !!(dynamicVoiceAssistant?.opening_greeting || dynamicVoiceAssistant?.screening_script)
            );

            if (assistantId && allAssistants.length > 0) {
              const matchedAssistant = allAssistants.find(
                (a) => a.id === assistantId
              );

              if (matchedAssistant) {
                const langId = matchedAssistant.language_id;

                // Preselect language
                setSelectedLanguage(langId);
                languageSet = true;

                // Filter assistants for this language client-side
                const filteredAssistants = allAssistants.filter(
                  (a) => a.language_id === langId
                );
                setVoiceAssistants(filteredAssistants);

                setNewQuestion((prev) => ({
                  ...prev,
                  voice_call_id: matchedAssistant.id,
                  voice_call_name: matchedAssistant.voice_name,
                  additional_questions: additionalQuestions,
                  custom_voice_call_qs_generation: customPrompt,
                  custom_voice_call_evaluation_weights: weights,
                }));
              }
            } else {
              setNewQuestion((prev) => ({
                ...prev,
                voice_call_id: "",
                voice_call_name: "",
                additional_questions: additionalQuestions,
                custom_voice_call_qs_generation: customPrompt,
                custom_voice_call_evaluation_weights: weights,
              }));
            }
          }
        }

        // If no language was pre-selected from job data, auto-select the default language
        if (!languageSet && langRes?.success) {
          const hasDefaultLang = langRes.data.some((l) => l.id === DEFAULT_LANG_ID);
          if (hasDefaultLang) {
            setSelectedLanguage(DEFAULT_LANG_ID);
            const filteredRes = await dispatch(fetchVoiceAssistants(DEFAULT_LANG_ID));
            if (filteredRes?.success) {
              setVoiceAssistants(filteredRes.data);
            }
          }
        }
      } catch (error) {
        console.error("Error in initial fetch:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    initialfetch();
    // eslint-disable-next-line
  }, [jobId]);

  const validateAndSave = async () => {
    if (!newQuestion.voice_call_id) {
      showSnackbar("Please select any voice assistant before saving");
      return;
    }

    const weights = newQuestion.custom_voice_call_evaluation_weights;
    const totalWeights =
      weights.communication + weights.technical + weights.cultural_fit;

    if (totalWeights < 100) {
      showSnackbar("Total weight must be exactly 100 (currently less than 100)");
      return;
    }
    if (totalWeights > 100) {
      showSnackbar("Total weight must be exactly 100 (currently more than 100)");
      return;
    }

    // NEW VALIDATION
    if (customInstructions.trim() && !enableInstructions) {
      showSnackbar(
        "Please check 'Use these custom instructions...' if you want to save instructions"
      );
      return;
    }

    setSaveLoading(true);
    try {
      const voiceStage = job?.hiring_stages?.find(
        (stage) =>
          stage?.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
      );
      const stageId = voiceStage?.id;

      if (!stageId) {
        showSnackbar("Voice Call stage not found");
        setSaveLoading(false);
        return;
      }

      const payload = {
        data: {
          assistant_id: newQuestion.voice_call_id,
          voice_call_name: newQuestion.voice_call_name,
          custom_voice_call_evaluation_weights: weights,
        },
      };

      if (newQuestion.additional_questions?.trim()) {
        payload.data.additional_questions =
          newQuestion.additional_questions.trim();
      }

      if (newQuestion.custom_voice_call_qs_generation?.trim()) {
        payload.data.custom_voice_call_qs_generation =
          newQuestion.custom_voice_call_qs_generation.trim();
      }

      if (enableInstructions) {
        payload.data.enable_instructions = true;
        payload.data.custom_instructions = customInstructions.trim();
      } else {
        payload.data.enable_instructions = false;
      }

      if (customLogisticsEnabled) {
        const filteredQuestions = customLogistics.filter((q) => q.trim() !== "");
        if (filteredQuestions.length === 0) {
          showSnackbar(
            "At least enter one logistic question or remove custom logistic checkbox"
          );
          setSaveLoading(false);
          return;
        }
        payload.data.custom_logistic_questions_enabled = true;
        payload.data.custom_logistic_questions = filteredQuestions.map((q) => ({
          question: q.trim(),
        }));
      } else {
        payload.data.custom_logistic_questions_enabled = false;
      }

      const result = await dispatch(
        updateHiringAssistaniData(job.id, stageId, payload)
      );

      if (result?.success !== false) {
        showSnackbar("Assistant updated successfully!", "success");
        initialfetch();
        resetForm();
        setType("");
      } else {
        showSnackbar(result?.message || "Failed to update assistant");
      }
    } catch (err) {
      showSnackbar("Error while saving assistant");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSliderChange = (field, value) => {
    value = parseInt(value, 10);

    const newValues = {
      ...newQuestion.custom_voice_call_evaluation_weights,
      [field]: value,
    };

    const total =
      newValues.communication + newValues.technical + newValues.cultural_fit;

    if (total <= 100) {
      setNewQuestion({
        ...newQuestion,
        custom_voice_call_evaluation_weights: newValues,
      });
    }
  };

  const getVoiceStage = () =>
    job?.hiring_stages?.find(
      (stage) => stage?.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
    );

  const handleToggleDynamicVoice = async (checked) => {
    setDynamicVoiceEnabled(checked);

    const voiceStage = getVoiceStage();
    if (!voiceStage?.id) return;

    const prevDynamicVoiceAssistant = voiceStage?.data?.dynamic_voice_assistant || {};

    const mergedData = {
      ...(voiceStage.data || {}),
      telephonic_call_mode: checked ? "DYNAMIC_VOICE_CALL" : "STANDARD_VOICE_CALL",
      dynamic_voice_assistant: {
        ...prevDynamicVoiceAssistant,
        enabled: checked,
      },
    };

    const result = await dispatch(
      updateJobHiringStageSettings(job.id, voiceStage.id, { data: mergedData })
    );

    if (result?.success === false) {
      showSnackbar(result?.message || "Failed to update call mode");
      setDynamicVoiceEnabled(!checked);
      return;
    }

    initialfetch();
  };

  const handleGeneratePrompt = async () => {
    if (!dynamicVoiceInputText.trim()) {
      showSnackbar("Please enter some text in the text area before generating prompts.");
      return;
    }

    setShowPromptEditor(true);
    setGeneratingPrompt(true);
    try {
      const formData = new FormData();
      formData.append("input_text", dynamicVoiceInputText.trim());

      const result = await dispatch(generateDynamicVoicePrompt(formData));
      if (result?.success) {
        const data = result.data || {};
        setDynamicVoicePrompt({
          opening_greeting: data.opening_greeting || "",
          screening_script:
            data.screening_script ||
            [data.branching_rules, data.screening_flow].filter(Boolean).join("\n\n"),
          end_call_rules: data.end_call_rules || "",
          compiled_prompt: data.compiled_prompt || "",
        });
      } else {
        showSnackbar(result?.message || "Failed to generate prompts");
        setShowPromptEditor(false);
      }
    } catch (err) {
      showSnackbar("Error while generating prompts");
      setShowPromptEditor(false);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const handleSaveDynamicPrompt = async () => {
    if (!dynamicVoicePrompt.opening_greeting.trim() || !dynamicVoicePrompt.screening_script.trim()) {
      showSnackbar("Generate a prompt before saving");
      return;
    }

    if (evaluationCriteriaEnabled && !evaluationFitStrategy.trim()) {
      showSnackbar("Please enter evaluation criteria before saving");
      evaluationCriteriaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      evaluationCriteriaRef.current?.focus();
      return;
    }

    const voiceStage = getVoiceStage();
    if (!voiceStage?.id) {
      showSnackbar("Voice Call stage not found");
      return;
    }

    setSavingDynamicPrompt(true);
    try {
      const prevAssistant = voiceStage?.data?.dynamic_voice_assistant || {};
      const mergedData = {
        ...(voiceStage.data || {}),
        telephonic_call_mode: "DYNAMIC_VOICE_CALL",
        dynamic_voice_assistant: {
          ...prevAssistant,
          enabled: true,
          opening_greeting: dynamicVoicePrompt.opening_greeting,
          screening_script: dynamicVoicePrompt.screening_script,
          end_call_rules: dynamicVoicePrompt.end_call_rules,
          compiled_prompt: dynamicVoicePrompt.compiled_prompt,
          version: typeof prevAssistant.version === "number" ? prevAssistant.version + 1 : 1,
          evaluation: {
            enabled: evaluationCriteriaEnabled,
            labels: EVALUATION_LABELS,
            evaluation_fit_strategy: evaluationCriteriaEnabled
              ? evaluationFitStrategy.trim()
              : "",
          },
        },
      };

      const result = await dispatch(
        updateJobHiringStageSettings(job.id, voiceStage.id, { data: mergedData })
      );

      if (result?.success === false) {
        showSnackbar(result?.message || "Failed to save prompt to hiring stage");
        return;
      }

      showSnackbar("Prompt saved to hiring stage!", "success");
      setDynamicVoiceInputText("");
      initialfetch();
    } catch (err) {
      showSnackbar("Error while saving prompt");
    } finally {
      setSavingDynamicPrompt(false);
    }
  };

  const openPromptModal = (title, field) => setPromptModal({ open: true, title, field });
  const closePromptModal = () => setPromptModal({ open: false, title: "", field: null });

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => {
            resetForm();
            setType("");
            stopAudio();
          }}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title d-flex align-items-center">
          <h3 style={{ margin: "0" }}>Telephonic Screening</h3>
        </div>
      </div>

      <div className="job-settings-body">
        {loading ? (
          <div
            className="loading-overlay"
            style={{
              position: "relative",
              marginTop: 16,
              alignItems: "start",
              height: "32px",
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="job-settings-form" style={{ width: "auto" }}>
              <div className="job-settings-form-question">
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <label className={`toggle-switch toggle-pink${dynamicVoiceEnabled ? " checked" : ""}`}>
                    <input
                      type="checkbox"
                      checked={dynamicVoiceEnabled}
                      onChange={(e) => handleToggleDynamicVoice(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                  <h5 style={{ margin: 0 }}>Enable Dynamic Voice Assistant (AI-generated screening call)</h5>
                </div>
                <p>When enabled, an AI assistant conducts the call using a generated screening script instead of the standard assistant settings below.</p>
              </div>

              {dynamicVoiceEnabled && (
                <div className="dynamic-voice-panel">
                  <div className="dynamic-voice-col">
                    <div className="prompt-block-header">
                      <label>Generate Prompt</label>
                    </div>
                    <textarea
                      className="dynamic-voice-textarea"
                      value={dynamicVoiceInputText}
                      onChange={(e) => setDynamicVoiceInputText(e.target.value)}
                      placeholder="Enter job description, screening notes, or any details to generate the prompt from..."
                    />
                    <button
                      type="button"
                      className="dynamic-voice-btn"
                      onClick={handleGeneratePrompt}
                    >
                      {generatingPrompt ? "Generating..." : "Generate Prompts"}
                    </button>
                  </div>

                  {showPromptEditor && (
                    <div className="dynamic-voice-col">
                      {generatingPrompt ? (
                        <div className="dynamic-voice-loading">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="prompt-block-header">
                            <label>Opening Greeting</label>
                            <button
                              type="button"
                              className="expand-icon-btn"
                              aria-label="Expand opening greeting"
                              onClick={() => openPromptModal("Opening Greeting", "opening_greeting")}
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                              </svg>
                            </button>
                          </div>
                          <textarea
                            className="dynamic-voice-textarea dynamic-voice-textarea-sm"
                            value={dynamicVoicePrompt.opening_greeting}
                            onChange={(e) =>
                              setDynamicVoicePrompt((prev) => ({
                                ...prev,
                                opening_greeting: e.target.value,
                              }))
                            }
                            placeholder="Generated opening greeting will appear here..."
                          />

                          <div className="prompt-block-header" style={{ marginTop: 16 }}>
                            <label>Screening Script</label>
                            <button
                              type="button"
                              className="expand-icon-btn"
                              aria-label="Expand screening script"
                              onClick={() => openPromptModal("Screening Script", "screening_script")}
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                              </svg>
                            </button>
                          </div>
                          <textarea
                            className="dynamic-voice-textarea dynamic-voice-textarea-sm"
                            value={dynamicVoicePrompt.screening_script}
                            onChange={(e) =>
                              setDynamicVoicePrompt((prev) => ({
                                ...prev,
                                screening_script: e.target.value,
                              }))
                            }
                            placeholder="Generated screening script will appear here..."
                          />

                          <button
                            type="button"
                            className="dynamic-voice-btn"
                            onClick={handleSaveDynamicPrompt}
                            disabled={savingDynamicPrompt}
                            style={{ marginTop: 16 }}
                          >
                            {savingDynamicPrompt ? "Saving..." : "Save"}
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {showPromptEditor && !generatingPrompt && (
                    <div className="dynamic-voice-col">
                      <div className="prompt-block-header">
                        <label className="d-flex align-items-center gap-1">
                          Evaluation Criteria
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              <span>
                                Describe when the AI should classify a candidate as{" "}
                                <b>EXCELLENT_FIT</b>, <b>GOOD_FIT</b>, or <b>NOT_FIT</b>{" "}
                                based on their screening call answers. This strategy is
                                used to evaluate the candidate's overall fit after the
                                call ends.
                              </span>
                            }
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: "1px solid #8a8a8a",
                                color: "#8a8a8a",
                                fontSize: 12,
                                fontStyle: "italic",
                                fontFamily: "serif",
                                cursor: "pointer",
                              }}
                            >
                              i
                            </span>
                          </Tooltip>
                        </label>
                        <label className={`toggle-switch toggle-pink${evaluationCriteriaEnabled ? " checked" : ""}`}>
                          <input
                            type="checkbox"
                            checked={evaluationCriteriaEnabled}
                            onChange={(e) => setEvaluationCriteriaEnabled(e.target.checked)}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </div>
                      {evaluationCriteriaEnabled && (
                        <>
                          <textarea
                            ref={evaluationCriteriaRef}
                            className="dynamic-voice-textarea dynamic-voice-textarea-sm"
                            value={evaluationFitStrategy}
                            onChange={(e) => setEvaluationFitStrategy(e.target.value)}
                            placeholder="Describe when to use EXCELLENT_FIT, GOOD_FIT, and NOT_FIT based on answers..."
                          />
                          <button
                            type="button"
                            className="dynamic-voice-btn"
                            onClick={handleSaveDynamicPrompt}
                            disabled={savingDynamicPrompt}
                            style={{ marginTop: 16 }}
                          >
                            {savingDynamicPrompt ? "Saving..." : "Save"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {promptModal.open && (
                <div className="prompt-modal open">
                  <div className="prompt-modal-backdrop" onClick={closePromptModal} />
                  <div className="prompt-modal-card">
                    <div className="prompt-modal-header">
                      <h3>{promptModal.title}</h3>
                      <button
                        type="button"
                        className="prompt-modal-close"
                        onClick={closePromptModal}
                        aria-label="Close"
                      >
                        &times;
                      </button>
                    </div>
                    <div className="prompt-modal-body">
                      <textarea
                        value={promptModal.field ? dynamicVoicePrompt[promptModal.field] : ""}
                        onChange={(e) => {
                          const field = promptModal.field;
                          if (!field) return;
                          setDynamicVoicePrompt((prev) => ({
                            ...prev,
                            [field]: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!dynamicVoiceEnabled && (
              <>
              <div className="job-settings-form-header flex-column">
                <div className="job-settings-form-header flex-column" style={{ gap: '6px' }}>
                  <label>Select Language</label>
                  <select
                    value={selectedLanguage}
                    onChange={async (e) => {
                      stopAudio();

                      const langId = e.target.value;
                      setSelectedLanguage(langId);
                      setVoiceAssistants([]);
                      setNewQuestion((prev) => ({
                        ...prev,
                        voice_call_id: "",
                        voice_call_name: "",
                      }));

                      if (langId) {
                        setAssistantsLoading(true);
                        const res = await dispatch(fetchVoiceAssistants(langId));
                        if (res?.success) {
                          setVoiceAssistants(res.data);
                        }
                        setAssistantsLoading(false);
                      }
                    }}
                    style={{ width: "300px" }}
                  >
                    <option value="">Select Language</option>
                    {languages?.map((lang) => (
                      <option key={lang.id} value={lang.id}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="job-settings-form-header flex-column" style={{ gap: '6px' }}>
                  <label>Select default voice assistant for this job</label>
                  {assistantsLoading ? (
                    <div className="d-flex align-items-center" style={{ height: "40px", width: "300px", paddingLeft: "10px" }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <select
                        value={newQuestion.voice_call_id}
                        onChange={(e) => {
                          const selected = voiceAssistants.find(
                            (a) => a.id === e.target.value
                          );
                          setNewQuestion({
                            ...newQuestion,
                            voice_call_id: selected?.id || "",
                            voice_call_name: selected?.voice_name || "",
                          });
                          stopAudio();
                        }}
                        style={{ width: "300px", cursor: !selectedLanguage ? "not-allowed" : "pointer" }}
                        disabled={!selectedLanguage}
                      >
                        <option value="">Select Assistant</option>
                        {voiceAssistants?.map((assistant) => (
                          <option key={assistant.id} value={assistant.id}>
                            {assistant.voice_name}
                          </option>
                        ))}
                      </select>

                      {(newQuestion.voice_call_id && selectedLanguage) && (
                        (() => {
                          const selectedAssistant = voiceAssistants.find(a => a.id === newQuestion.voice_call_id);
                          const audioUrl = selectedAssistant?.voice_clip_url;

                          if (audioUrl) {
                            return (
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  backgroundColor: "#F75891",
                                  cursor: "pointer",
                                  border: "1px solid #F75891",
                                }}
                                title='Play/Pause'
                                onClick={() => handleAudio(audioUrl)}
                              >
                                {playingAudioUrl === audioUrl ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                  </svg>
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Instructions */}
              <div className="job-settings-form-question">
                <div className="d-flex align-items-center">
                  <h5>Custom Instructions</h5>
                </div>
                <p>Note: AI Assistant will ask upto 5 questions based on these instructions</p>
                <textarea
                  rows="6"
                  placeholder="Enter custom instructions here..."
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                />
                <label>
                  <input
                    type="checkbox"
                    checked={enableInstructions}
                    onChange={(e) => { setEnableInstructions(e.target.checked) }}
                    style={{ width: "unset", height: "unset" }}
                  />{" "}
                  Use these custom instructions as part of AI Voice Interview
                </label>
              </div>

              {/* logistic questions */}
              <div className="job-settings-form-question">
                <div className="d-flex align-items-center">
                  <label>
                    <input
                      type="checkbox"
                      checked={customLogisticsEnabled}
                      onChange={(e) => {
                        setCustomLogisticsEnabled(e.target.checked);
                        if (e.target.checked && customLogistics.length === 0) {
                          setCustomLogistics([""]);
                        }
                      }}
                      style={{ width: "unset", height: "unset" }}
                    />{" "}
                    Logistic Questions
                  </label>
                </div>

                {customLogisticsEnabled && (
                  <div className="d-flex flex-column gap-3 mt-2">
                    {customLogistics.map((q, index) => (
                      <div className="logistic-input" key={index}>
                        <label>{index + 1}. Question</label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="text"
                            value={q}
                            onChange={(e) => {
                              const updated = [...customLogistics];
                              updated[index] = e.target.value;
                              setCustomLogistics(updated);
                            }}
                            placeholder="Enter logistic question"
                          />
                          {customLogistics.length > 1 && (
                            <img
                              src="/images/cross-pink.svg"
                              alt="remove"
                              width={14}
                              height={14}
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                const updated = [...customLogistics];
                                updated.splice(index, 1);
                                setCustomLogistics(updated);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    {customLogistics.length < 2 && (
                      <button
                        type="button"
                        className="logistic-btn"
                        onClick={() =>
                          setCustomLogistics([...customLogistics, ""])
                        }
                      >
                        + Add Question
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Sliders Section */}
              <div className="job-settings-form-question">
                <label>Evaluation Weightage</label>
                <p>Customize the weightage criteria for calculation of final score</p>
                <div
                  className="d-flex flex-column"
                  style={{ maxWidth: "400px" }}
                >
                  {/* Communication */}
                  <div>
                    <label>
                      Communication:{" "}
                      {
                        newQuestion.custom_voice_call_evaluation_weights
                          .communication
                      }
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={
                        newQuestion.custom_voice_call_evaluation_weights
                          .communication
                      }
                      onChange={(e) =>
                        handleSliderChange("communication", e.target.value)
                      }
                      style={{ height: "unset" }}
                    />
                  </div>

                  {/* Cultural */}
                  <div>
                    <label>
                      Cultural:{" "}
                      {
                        newQuestion.custom_voice_call_evaluation_weights
                          .cultural_fit
                      }
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={
                        newQuestion.custom_voice_call_evaluation_weights
                          .cultural_fit
                      }
                      onChange={(e) =>
                        handleSliderChange("cultural_fit", e.target.value)
                      }
                      style={{ height: "unset" }}
                    />
                  </div>

                  {/* Technical */}
                  <div>
                    <label>
                      Technical:{" "}
                      {
                        newQuestion.custom_voice_call_evaluation_weights
                          .technical
                      }
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={
                        newQuestion.custom_voice_call_evaluation_weights
                          .technical
                      }
                      onChange={(e) =>
                        handleSliderChange("technical", e.target.value)
                      }
                      style={{ height: "unset" }}
                    />
                  </div>
                </div>

                {/* Warning text */}
                <p
                  style={{
                    color:
                      newQuestion.custom_voice_call_evaluation_weights.communication +
                        newQuestion.custom_voice_call_evaluation_weights.technical +
                        newQuestion.custom_voice_call_evaluation_weights.cultural_fit !==
                        100
                        ? "red"
                        : "green",
                  }}
                >
                  Total combined value of weight distribution must be exactly 100 (Current:{" "}
                  {newQuestion.custom_voice_call_evaluation_weights.communication +
                    newQuestion.custom_voice_call_evaluation_weights.technical +
                    newQuestion.custom_voice_call_evaluation_weights.cultural_fit}
                  )
                </p>
              </div>

              <div className="d-flex gap-4 align-items-center">
                <button
                  className="save-btn"
                  type="button"
                  onClick={validateAndSave}
                  disabled={saveLoading}
                >
                  {saveLoading ? "Saving..." : "Save"}
                </button>
                <h6
                  className="cursor-pointer cancle-txt"
                  onClick={() => {
                    resetForm();
                    setType("");
                    stopAudio();
                  }}
                >
                  Cancel
                </h6>
              </div>
              </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Assistant form */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TeleScreening;
